import { find, isNumber, isString, lowerCase, replace, split, startsWith, upperCase, upperFirst } from 'lodash';
import numeral from 'numeral';
import {
  COUNTRIES_HAVE_STATE,
  EmptyText,
  LOGISTICS_NAME_PATTERNS,
  NAText,
  WEBSITE_PROTOCOL,
} from 'src/utils/constants/config';
import { COUNTRIES_LIST_FOR_FIND_LOGIC } from 'src/utils/constants/countries';
import { CompanyStatusCode, OtherContactStatusCode, TypePlanPrice } from 'src/utils/constants/enum';

export const getCurrentUrl = () => {
  if (window?.location) {
    return window.location.origin + window.location.pathname;
  }
  return '';
};

export const getOriginUrl = () => {
  if (window?.location) {
    return window.location.origin;
  }
  return '';
};

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const formatUrlNonProtocol = (url?: string | null) => {
  if (!url) {
    return '';
  }
  if (url && !startsWith(url, WEBSITE_PROTOCOL.HTTP) && !startsWith(url, WEBSITE_PROTOCOL.HTTPS)) {
    return `${WEBSITE_PROTOCOL.HTTPS}${url}`;
  }
  return url;
};

export const formatPhoneNumberBeforeSent = (phoneCode?: string, number?: string) => {
  if (!phoneCode || !number) {
    return '';
  }
  if (number.charAt(0) === '0') {
    return phoneCode + ' ' + number.slice(1).replaceAll(' ', '');
  }
  return phoneCode + ' ' + number.replaceAll(' ', '');
};

export const getDefaultPhoneNumber = (phoneNumber?: string) => {
  if (!phoneNumber) {
    return { phoneCode: '', phoneNumber: '' };
  }
  return {
    phoneCode: split(phoneNumber, ' ')[0] || '',
    phoneNumber: split(phoneNumber, ' ')[1] || '',
  };
};

export const getCountryByPhoneCode = (phoneCode: string) => {
  if (!phoneCode) {
    return {
      code: '',
      label: '',
      phone: '',
    };
  }
  let country;
  if (startsWith(phoneCode, '+')) {
    country = find(COUNTRIES_LIST_FOR_FIND_LOGIC, (o) => '+' + o.phone === phoneCode);
  } else {
    country = find(COUNTRIES_LIST_FOR_FIND_LOGIC, (o) => '+' + o.phone === phoneCode);
  }

  if (!country) {
    return {
      code: '',
      label: '',
      phone: '',
    };
  }

  return country;
};

export const getCountryByCountryCode = (countryCode?: string | null) => {
  if (!countryCode) {
    return {
      code: '',
      label: '',
      phone: '',
    };
  }
  const country = find(COUNTRIES_LIST_FOR_FIND_LOGIC, (o) => upperCase(o.code) === upperCase(countryCode));

  if (!country) {
    return {
      code: '',
      label: '',
      phone: '',
    };
  }

  return country;
};

export const getColorCompanyStatus = (status_code: CompanyStatusCode) => {
  switch (status_code) {
    case CompanyStatusCode.Success:
      return 'success';
    case CompanyStatusCode.Error:
      return 'error';
    case CompanyStatusCode.Warning:
      return 'warning';
    case CompanyStatusCode.Unknown:
      return 'default';
    default:
      return 'default';
  }
};

export const getColorContactStatus = (status_code: OtherContactStatusCode) => {
  switch (status_code) {
    case OtherContactStatusCode.Valid:
      return 'success';
    case OtherContactStatusCode.Invalid:
      return 'warning';
    case OtherContactStatusCode.Risky:
      return 'error';
    case OtherContactStatusCode.Unknown:
      return 'default';
    default:
      return 'default';
  }
};

export const getColorPercentMatch = (percentMatch: number | null) => {
  if (!isNumber(percentMatch)) return 'default';
  if (isNumber(percentMatch) && percentMatch >= 80) return 'success';
  if (isNumber(percentMatch) && percentMatch < 80 && percentMatch >= 25) return 'warning';
  return 'error';
};

export const renderTextOrDefault = (text?: string | number | null) => {
  if ((isString(text) && text !== '') || isNumber(text)) {
    return text;
  }
  return EmptyText;
};

export const renderNumberOrDefault = (number?: number | null, defaultText?: string) => {
  if (!isNumber(number)) return defaultText || EmptyText;

  return numeral(number).format('0,0.[0]');
};

export const renderPriceOrDefault = (number?: number | null) => {
  if (!isNumber(number)) return EmptyText;

  return numeral(number).format('0,0.[00]');
};

export const renderValueNumberTrade = (number?: number | null, defaultText?: ExpectedAny) => {
  if (!isNumber(number) || number === 0) return defaultText || NAText;

  return numeral(number).format('0,0.[0]');
};

export const convertSnakeCaseToUpperFirst = (text: string) => {
  return lowerCase(text)
    .split('_')
    .map((text) => upperFirst(text))
    .join(' ');
};

export const convertToStartCase = (text?: string) => {
  return (text || '')
    .toLowerCase()
    .split(' ')
    .map((text) => upperFirst(text))
    .join(' ');
};

export const convertCamelCaseToNormalText = (str: string) => {
  return str
    .replace(/([A-Z])/g, ' $1') // Thêm khoảng trắng trước chữ in hoa
    .replace(/^./, (char) => char.toUpperCase()) // Viết hoa ký tự đầu tiên
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Viết hoa mỗi từ
    .join(' ');
};

export const getCompanyAgeText = (company_age?: number | null) => {
  if (!isNumber(company_age)) return EmptyText;

  return company_age > 1 ? `${company_age} years` : `${company_age} year`;
};

export const checkCountryHaveState = (country_code?: string) => {
  if (!country_code) return false;
  return COUNTRIES_HAVE_STATE.includes(country_code.toUpperCase());
};

export const getTimeFromPlanType = (type: string) => {
  switch (type) {
    case TypePlanPrice.Month:
      return 1;
    case TypePlanPrice.Quarter:
      return 3;
    case TypePlanPrice.HalfYear:
      return 6;
    default:
      return 12;
  }
};

export const getPlanTypeFromTime = (time: number) => {
  switch (time) {
    case 1:
      return TypePlanPrice.Month;
    case 3:
      return TypePlanPrice.Quarter;
    case 6:
      return TypePlanPrice.HalfYear;
    case 12:
      return TypePlanPrice.Year;
    default:
      return '';
  }
};
export const getStringOrDefault = (s: ExpectedAny, defaultValue = '') => {
  if (isString(s)) return s;
  return defaultValue ?? EmptyText;
};

export const reverseString = (s: string) => {
  return s.split('').reverse().join('');
};

export const normalizeHSCodeToHS6 = (hsCode?: string) => {
  if (!hsCode) return '';
  const truncatedZeroesHsCode = reverseString(parseInt(reverseString(hsCode)).toString());
  return truncatedZeroesHsCode.padEnd(
    Math.max(6, truncatedZeroesHsCode.length + (truncatedZeroesHsCode.length % 2)),
    '0',
  );
};

export const formatJurisdictionName = (jurisdiction: string, countryCode: string) => {
  if (startsWith(jurisdiction, `${countryCode}.`)) {
    return replace(jurisdiction || '', `${countryCode}.`, '');
  }
  return jurisdiction || '';
};

export const checkCompanyLogistics = (company_name?: string) => {
  if (!company_name) return false;
  const lowerName = lowerCase(company_name);
  return find(LOGISTICS_NAME_PATTERNS, (pattern) => lowerName.includes(lowerCase(pattern)));
};
